// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { connect, useSelector } from 'react-redux'
import {
  Table,
  Row,
  Col,
  Button,
  Modal,
  Tooltip,
  Space,
  Progress,
  Typography,
  Pagination,
  Form,
  DatePicker, TimePicker
} from 'antd'
import moment from 'moment'
import cx from 'classnames'
import ModifyJobModal from './ModifyJobModal'
import './style.less'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { Dispatch, RootState } from 'store'
import { AllStages, JobStatus, JobType, stageAndStatus, uploadTypeDictionary, platFormDictionary } from 'constants/job'
import {
  ActionCreator,
  FilterSwitch,
  Help,
  JobDetail,
  JobStatusCell,
  StatusBar,
  UploadQueueFilter
} from 'components/upload-queue'
import { IJob, ITask } from 'api/jobs'
import { convertTimezone } from 'utils/time'
import { debounce, sumBy } from 'lodash'
import { PhoneOutlined } from '@ant-design/icons'
import {EnvironmentsOptions, LiteGroupDeviceDirectionNames, OldEnvironmentsOptions} from '../../constants/device'
import UploadModal from '../../components/image-upload/UploadModal'

import { JobGroupTable } from './JobGroupTable'
const stagesOptions = [
  { value: '0', label: 'Not Started', status: [JobStatus.Pending] },
  { value: '1', label: 'In Progress', status: [JobStatus.Processing] },
  { value: '2', label: 'Completed', status: [JobStatus.Successful] }
]
const stages = stagesOptions.reduce((prev, cur) => [...prev, cur.label], [])
const TaskColumn = ({ data, actived }):any => {
  const titleStyle = cx('row', { actived })
  return (
    <div className={titleStyle}>
      <PhoneOutlined
        className={cx('platform', { mobile: data.creatorPlatform === 2, web: data.creatorPlatform === 1 })}
      />
      <div className='row-primary'>
        <span>{data.jobName}</span>
      </div>
      <div className='row-secondary'>
        {moment(data.startTime, 'HH:mm:ss').format('hh:mm A')}-{moment(data.endTime, 'HH:mm:ss').format('hh:mm A')}
        &nbsp;,&nbsp;
        {moment(data.startDate).format('D/M/YYYY')}-{moment(data.endDate).format('D/M/YYYY')}
      </div>
    </div>
  )
}
function mapState (state: RootState) {
  return {
    city: state.city.current,
    ...state.jobList,
    loading: state.loading.effects.jobList.fetchJobs,
    uploadCameraLoading: state.loading.effects.download.updateDownloadTask
  }
}

function mapDispatch (dispatch: Dispatch) {
  return {
    ...dispatch.jobList,
    // setDownloadPendingListShow: dispatch.download.setDownloadPendingListShow,
    updateEditingPreTask: dispatch.download.updateEditingPreTask,
    updateDownloadTask: dispatch.download.updateDownloadTask,
  }
}

export type IUploadQueueProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & RouteComponentProps
const UploadQueue = props => {
  const [activeRecord, setActiveRecord] = useState(null)
  const [showFilter, setShowFilter] = useState(true)
  const [currentStage, setCurrentStage] = useState('1')
  const [showJobDetail, setShowJobDetail] = useState(false)
  const [refreshTag, setRefreshTag] = useState({});
  const [uploadingCamera, setUploadingCamera] = useState(null)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const ref = useRef()
  const userInfo = useSelector(state => state.auth.userInfo)

  useEffect(() => {
    let {
      fetchJobs,
      city: { id: cityID },
      filters = {},
      pagination,
      setFilters
    } = props
    // when changing city
    if (ref.current && ref.current !== cityID) {
      // closeJobDetail()
      const status = stageAndStatus[currentStage]
      filters = {
        ...filters,
        creator: [userInfo.username],
        jobType: [],
        deviceType: [],
        jobName: '',
        creatorPlatform: undefined,
        status,
        startDate: undefined,
        endDate: undefined
      }
      setFilters(filters)
      ref.current = cityID
      return
    }
    // when first loaded
    if (!ref.current) {
      setFilters({ creator: [userInfo.username] })
      ref.current = cityID
      return
    }
    const { current, pageSize } = pagination
    fetchJobs({
      pageIndex: current,
      pageSize: pageSize,
      cityID,
      filters: {
        ...filters,
      }
    })
  }, [props.filters, props.city.id, userInfo, props.pagination.current, refreshTag])

  useEffect(() => {
    const { setFilters,pagination, setPagination } = props
    const status = stageAndStatus[currentStage]
    const { current, pageSize } = pagination
    setFilters({ status })
    setPagination({current: 1})
  }, [currentStage])

  const isSameRecord = useCallback((a, b) => {
    if (!a || !b) {
      return false
    }
    return a.jobID === b.jobID
  }, [])

  const getRowClassName = useCallback(
    (record, index) => {
      if (isSameRecord(record, activeRecord)) {
        return 'ant-table-row-selected'
      } else {
        return ''
      }
    },
    [activeRecord]
  )

  const handleRowClick = useCallback(record => {
    setShowJobDetail(true)
    const oldRecord = activeRecord
    if (!oldRecord) {
      setActiveRecord(record)
    } else if (!isSameRecord(oldRecord, record)) {
      setActiveRecord(record)
    }
  }, [])

  const handleClickDelete = useCallback(
    (record, event) => {
      const { deleteJob, list, fetchJobs } = props
      event.stopPropagation()
      Modal.confirm({
        title: 'Do you want to delete this job?',
        content: '',
        onOk: async () => {
          await deleteJob(record.jobID)
          if (list.length === 0) {
            fetchJobs({
              pageIndex: 1,
              pageSize: 10
            })
          }
        }
      })
    },
    [props.list]
  )

  const handleClickModify = useCallback((record, event) => {
    event.stopPropagation()
    setUploadingCamera(record)

  }, [])

  const getInitialValues = (record) => {
    if(!record) return []
    const {startDate, endDate, startTime, endTime} = record;
    const initialValues = {
      uploadDate: [moment(startDate,'YYYY-MM-DD'),moment(endDate,'YYYY-MM-DD')],
      uploadTime: [moment(startTime,'HH:mm:ss'),moment(endTime,'HH:mm:ss')]
    }
    return initialValues
  }

  const handleChangePage = useCallback(
    (current, pageSize) => {
      const { setPagination } = props
      setPagination({ current, pageSize })
    },
    [props.filters]
  )

  const handlePageShowSizeChange = useCallback(handleChangePage.bind(null, 1), [props.filters])

  const handlePopupClose = useCallback(() => {
    const { resetFilters, setDownloadPendingListShow } = props
    setDownloadPendingListShow(false)
    resetFilters()
  }, [])

  const handleModalCancel = useCallback(() => {
    props.setEditModalVisible(false)
  }, [])
  const handleUploadModalChange =  async (action)=>{
    const {type, payload} = action
    const {updateEditingPreTask, updateDownloadTask} = props
    switch (type){
      case 'cancel':
        setUploadingCamera(null)
        break
      case 'ok':
        updateEditingPreTask({...uploadingCamera,...payload})
        await updateDownloadTask()
        setUploadingCamera(null)
        setRefreshTag({})
        break
      default: break
    }
  }

  const handleModalOk = useCallback(value => {
    props.modifyJob(value)
  }, [])

  const handleFiltersChange = useCallback(value => {
    const { setFilters } = props
    setFilters(value)
  }, [])

  const handleClickStopJob = (record: IJob, event) => {
    const { stopJob } = props
    event.stopPropagation()
    Modal.confirm({
      title: 'Do you want to Stop this job?',
      content: '',
      onOk: async () => {
        await stopJob(record)
        .then(()=>{
          setRefreshTag({})
        })
        .catch(error => {
          if (error.response) {
            Modal.error({
              title: error.response.status,
              content: error.response.text
            })
          }
        })
      }
    })
  }
  const handleClickStopTask = useCallback(
    (record: ITask, event) => {
      const { stopTask } = props
      event.stopPropagation()
      Modal.confirm({
        title: 'Do you want to Stop this task',
        content: '',
        onOk: async () => {
          await stopTask({ job: activeRecord, task: record })
            .catch(error => {
            if (error.response) {
              Modal.error({
                title: error.response.status,
                content: error.response.text
              })
            }
          })
        }
      })
    },
    [activeRecord]
  )
  const handleJumpTo = (record: ITask, event) => {
    let envOption;
    envOption = EnvironmentsOptions.find(item => item.value === record.environment)
    event.stopPropagation()
    window.open(`${envOption.uploadUrl}/details/${record.sequenceID}`)
  }

  const handleClickResume = async (record: IJob) => {
    const { resumeJob, filters, fetchJobs, pagination } = props
    try {
      await resumeJob(record)
      fetchJobs({
        pageIndex: pagination.current,
        pageSize: pagination.pageSize,
        filters
      })
    } catch (error) {
      if (error.response) {
        Modal.error({
          title: error.response.status,
          content: error.response.text
        })
      }
    }
  }

  const onFilterChange = useCallback(e => {
    setShowFilter(e)
  }, [])
  const onStageChange = useCallback(({ value }) => {
    setCurrentStage(value)
  }, [])
  const onFilterDataChange = useCallback(
    debounce(
      (key, value) => {
        const { setFilters } = props
        const filters = Array.isArray(key) ? { [key[0]]: value[0], [key[1]]: value[1] } : { [key]: value }
        setFilters(filters)
      },
      1000,
      { leading: false }
    ),
    []
  )
  const closeJobDetail = useCallback(() => {
    setShowJobDetail(false)
  }, [])
  const actions = {
    edit: handleClickModify,
    stop: handleClickStopTask,
    delete: handleClickDelete,
    jumpTo: handleJumpTo
  }

  const {
    loading,
    list,
    pagination,
    isEditModalVisible,
    editingJob,
    modifyLoading,
    pausingJobID,
    resumingJobID,
    city,
    fetchTasks,
    uploadCameraLoading
  } = props
  const getColumns = useCallback(
    (mainTable: boolean) =>
      [
        {
          title: 'Date Created',
          dataIndex: 'createdAt',
          width: 120,
          stages: ['Not Started', 'In Progress', 'Completed'],
          render: t => {
            return convertTimezone(t, city.timezone)
          }
        },
        {
          title: 'Job ID',
          dataIndex: 'jobID',
          width: 80,
          stages: ['Not Started', 'In Progress', 'Completed'],
        },
        {
          title: 'Jobs',
          stages: ['Not Started', 'In Progress', 'Completed'],
          width: 220,
          render: (text, d:IJob):any => <TaskColumn data={d} actived={isSameRecord(d, activeRecord)} />
          // render: (text, d:IJob):any => <span>123</span>
        },
        {
          title: 'Device Type',
          stages: ['Not Started', 'In Progress', 'Completed'],
          width: mainTable ? 100 : 148,
          render: (text, d:IJob):any=> d.groupID === 0 ? 'Single' : mainTable ? '360Lite' : `360Lite (${LiteGroupDeviceDirectionNames[d.photoDirection]})`
          // render: (text, d:IJob):any => <span>123</span>
        },
        {
          title: 'Platform',
          stages: ['Not Started', 'In Progress', 'Completed'],
          width: 100,
          dataIndex: 'creatorPlatform',
          render: (text, d) => {
            const target = platFormDictionary.find(item => item.value === text)
            return target.label
          }
        },
        {
          title: 'Total upload Days',
          dataIndex: 'taskTotalCount',
          width: 120,
          stages: ['Not Started']
        },
        {
          title: 'Overall Status',
          stages: ['Completed'],
          width: 100,
          dataIndex: 'status',
          render: (t, d: IJob) => {
            return <JobStatusCell status={d.status} />
          }
        },
        {
          title: 'Uploaded Days',
          dataIndex: 'successTaskCount',
          width: 120,
          stages: ['In Progress', 'Completed'],
          render: (t, d: IJob) => {
            const { successTaskCount, taskTotalCount } = d
            const percent = Math.min((successTaskCount / taskTotalCount) * 100, 100)
            return (
              <div style={{ paddingRight: '30px' }}>
                <Progress
                  percent={Number(percent)}
                  status={'normal'}
                  format={() => (
                    <span>
                      {successTaskCount}&nbsp;of&nbsp;{taskTotalCount}&nbsp;
                    </span>
                  )}
                />
              </div>
            )
          }
        },
        {
          title: () => (
            <span>
              {'Total Photos'}
              <Tooltip title={'Total number of stitched/processed photos'}>
                <QuestionCircleOutlined style={{marginLeft: 8}} />
              </Tooltip>
            </span>
          ),
          stages: ['In Progress', 'Completed'],
          width: 120,
          dataIndex: 'uploadedImageCount'
        },
        {
          title: 'Creator',
          dataIndex: 'creatorName'
          , width: 120,
          stages: ['Not Started', 'In Progress','Completed'] ,
          render: (t,d:IJob)=>{
            const {creatorName} = d
            const creator = creatorName ||  '---' ;
            return <div>{creator}</div>
          }
        },
        {
          title: 'Actions',
          stages: ['Not Started', 'In Progress'],
          width: 120,
          render: (t, d: IJob, i) => {
            const actions = {
              edit: handleClickModify,
              stop: handleClickStopJob,
              delete: handleClickDelete,
              jumpTo: handleJumpTo
            }
            if (d.groupID === 0) {
              return <div>
                <ActionCreator type={`job:${d.status}`} actions={actions} target={d} />
              </div>
            } else {
              return null
            }
          }
        }
      ].filter(item => item.stages && item.stages.includes(stages[currentStage])),
    [currentStage, activeRecord]
  )
  const filterClassName = cx(['filter-container-outer', { show: showFilter }])
  const dataTemplate = {
    createdAt: {
      label: 'Date Created',
      value: '',
      func: item => {
        return convertTimezone(item, city.timezone)
      }
    },
    cityID: { label: 'Capture City', value: '', func: item => city.name },
    jobType: {
      label: 'Upload Type',
      value: '',
      func: item => {
        const target = uploadTypeDictionary.find(_item => _item.value === item)
        return target ? target.label : item
      }
    },
    startDate: { label: 'Start Date', value: '', func: item => item },
    endDate: { label: 'End Date', value: '', func: item => item },
    startTime: { label: 'Start Time', value: '', func: item => item },
    endTime: { label: 'End Time', value: '', func: item => item },
    uploadedImageCount: { label: 'Total Image Processed', value: '', func: item => item },
    creatorName: { label: 'Creator', value: '', func: item => item },
    creatorPlatform: {
      label: 'Platform',
      value: '',
      func: item => {
        const target = platFormDictionary.find(_item => _item.value === item)
        return target ? target.label : item
      }
    },
    orgCode: { label: 'Creator Org', value: '', func: item => item },
  }
  if (activeRecord) {
    Object.keys(dataTemplate).forEach(key => {
      dataTemplate[key].value = dataTemplate[key].func(activeRecord[key])
    })
  }
  const sumOfColumnsWidth = sumBy(getColumns(true), 'width')
  const initialValues = getInitialValues(uploadingCamera)
  const jobData = {jobID: activeRecord?.jobID, jobName: activeRecord?.jobName,jobDetails: {...dataTemplate},groupID: activeRecord?.groupID}
  
  const renderExpandedRow = (sumOfColumnsWidth: number) => (record: IJob, index: number, indent: number, expanded: boolean) => {
    return (
      <JobGroupTable
        job={record}
        key={`job-group-${index}`}
        scroll={{ x: sumOfColumnsWidth }}
        onRow={(record) => ({
          onClick: handleRowClick.bind(null, record)
        })}
        getColumns={() => getColumns(false)}
      />
    )
  }
  
  return (
    <>
      <div className='download-pending-list'>
        <Row justify={'space-between'}>
          <Col xs={{}} xxl={{ span: 4 }} lg={{ span: 4 }} md={{ span: 2 }}>
              <Typography.Title level={3}>Upload Queue</Typography.Title>
          </Col>
          <Col
            xs={{ span: 4 }}
            xxl={{ span: 10 }}
            xl={{ span: 12 }}
            lg={{ span: 16 }}
            sm={{ span: 4 }}
            md={{ span: 22 }}
          >
            <span className={'header-right'}>
              <StatusBar
                statusOptions={stagesOptions}
                title={'Upload Stage:'}
                value={currentStage}
                onChange={onStageChange}
              />
              <Help />
            </span>
          </Col>
        </Row>
        <div className={'table-header'}>
          <span className='sum'>
            {Boolean(list?.length) && `show ${list?.length} of ${props?.pagination?.total}`}
          </span>
          <span>
            <FilterSwitch active={showFilter} onChange={onFilterChange} />
          </span>
        </div>
        <div className={'content'}>
          <UploadQueueFilter
            className={filterClassName}
            onChange={onFilterDataChange}
            onFilterChange={onFilterChange}
            stage={currentStage}
          />
          <Table
            columns={getColumns(true)}
            className={'table'}
            tableLayout={'fixed'}
            onRow={record => ({
              onClick: () => {
                const { groupID, jobID } = record
                if (groupID !== 0) {
                  let keys = [...expandedRowKeys];
                  keys = keys.includes(jobID) ? keys.filter(k => k !== jobID) : [...keys, jobID]
                  setExpandedRowKeys(keys)
                } else {
                  handleRowClick(record)
                }
              }
            })}
            rowClassName={getRowClassName}
            dataSource={list}
            loading={loading}
            rowKey={d => d.jobID}
            scroll={{ x: sumOfColumnsWidth }}
            pagination={{
              onChange: handleChangePage,
              // onShowSizeChange: handlePageShowSizeChange,
              showSizeChanger: false,
              ...pagination,
              size: 'default'
            }}
            size='small'
            expandable={{
              expandIconColumnIndex: 3,
              rowExpandable: (record) => record.groupID !== 0,
              expandedRowClassName: () => 'job-table-expanded-row',
              expandedRowRender: renderExpandedRow(sumOfColumnsWidth),
              onExpandedRowsChange: (expandedRowKeys) => setExpandedRowKeys(expandedRowKeys),
              expandedRowKeys,
            }}
          />
        </div>
      </div>
      <JobDetail
        visible={showJobDetail}
        onClose={closeJobDetail}
        jobData={jobData}
        fetchTasks={fetchTasks}
        actions={actions}
      />
      <ModifyJobModal
        visible={isEditModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        job={editingJob}
        modifyLoading={modifyLoading}
      />
      <UploadModal
        title={`${uploadingCamera?.deviceID}`}
        visible={!!uploadingCamera}
        onChange={handleUploadModalChange}
        initialValues={initialValues}
        okButtonProps={{loading: uploadCameraLoading}}
      />
    </>
  )
}
export default withRouter(connect(mapState, mapDispatch)(UploadQueue))
